// ============================================================
//  NEWS — Firestore-Collection "news"
//  (Vormals "matchberichte" genannt — auf Wunsch umbenannt.
//  Die Firestore-Collection heisst intern weiterhin "matchberichte",
//  da die migrierten Daten dort liegen; nach aussen (UI, Routen)
//  heisst alles "News".)
//
//  Datenmodell:
//  {
//    title, slug, date (YYYY-MM-DD), opponent, score, text,
//    kategorie, gepinnt, autor,
//    photoUrls: [], videoUrl: "" (YouTube-Embed, optional),
//    authorUid, createdAt
//  }
// ============================================================
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  limit as fsLimit,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const COLLECTION = "matchberichte";

export const KATEGORIEN = ["B-Junioren TSS", "C-Junioren TSS", "Allgemein"];

/** Titel → URL-Slug (z.B. "Sieg gegen Buochs" + "2026-04-12" → "sieg-gegen-buochs-2026-04-12") */
export function slugify(title, date) {
  const basis = String(title || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return date ? `${basis}-${date}` : basis;
}

/** YouTube-Link (beliebiges Format) → Embed-URL mit modestbranding & rel=0 */
export function parseYoutubeUrl(url) {
  if (!url) return "";
  const trimmed = url.trim();
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([\w-]{11})/,
    /(?:youtu\.be\/)([\w-]{11})/,
    /(?:youtube\.com\/embed\/)([\w-]{11})/,
    /(?:youtube\.com\/shorts\/)([\w-]{11})/,
  ];
  for (const re of patterns) {
    const match = trimmed.match(re);
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}?modestbranding=1&rel=0`;
    }
  }
  return ""; // ungültiger Link → lieber leer als kaputt
}

/** Alle News, neueste zuerst */
export async function fetchNews() {
  const q = query(collection(db, COLLECTION), orderBy("date", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Die n neuesten News (z.B. für die Startseite) */
export async function fetchNeuesteNews(n = 3) {
  const q = query(collection(db, COLLECTION), orderBy("date", "desc"), fsLimit(n));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Einzelner News-Artikel per Slug */
export async function fetchNewsBySlug(slug) {
  const q = query(collection(db, COLLECTION), where("slug", "==", slug), fsLimit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

/** Einzeldokument per Firestore-ID (für Admin-Bearbeitung praktischer als Slug) */
export async function fetchNewsById(id) {
  const snap = await getDoc(doc(db, COLLECTION, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Neuen News-Artikel anlegen (Pflicht: title, date) */
export async function createNews(daten, authorUid) {
  if (!daten.title || !daten.date) {
    throw new Error("Titel und Datum sind Pflichtfelder.");
  }
  const payload = {
    title: daten.title,
    slug: slugify(daten.title, daten.date),
    date: daten.date,
    opponent: daten.opponent || "",
    score: daten.score || "",
    text: daten.text || "",
    kategorie: daten.kategorie || "",
    gepinnt: !!daten.gepinnt,
    autor: daten.autor || "",
    photoUrls: daten.photoUrls || [],
    videoUrl: daten.videoUrl ? parseYoutubeUrl(daten.videoUrl) : "",
    authorUid,
    createdAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, COLLECTION), payload);
  return ref.id;
}

/** Bestehenden News-Artikel aktualisieren */
export async function updateNews(id, daten) {
  const payload = { ...daten, gepinnt: !!daten.gepinnt };
  if (daten.videoUrl !== undefined) {
    payload.videoUrl = parseYoutubeUrl(daten.videoUrl);
  }
  if (daten.title && daten.date) {
    payload.slug = slugify(daten.title, daten.date);
  }
  await updateDoc(doc(db, COLLECTION, id), payload);
}

/** News-Artikel löschen (Fotos in Storage müssen separat gelöscht werden) */
export async function deleteNews(id) {
  await deleteDoc(doc(db, COLLECTION, id));
}
